// E-mail doorzoeken in de eigen mailbox via Microsoft Graph (delegated Mail.Read).
// Zelfde token-flow als OneNote (ingelogde gebruiker) — geen app-only.
import { acquireGraphToken } from "./graphToken";
import { htmlToPlainText } from "./htmlText";

const GRAPH = "https://graph.microsoft.com/v1.0";

/**
 * Zoek e-mails op een trefwoord (bijv. klantnaam of onderwerp).
 * Returnt [{ id, subject, from, received, preview, webLink }].
 */
export async function searchMail(query, { top = 15 } = {}) {
  const q = (query || "").trim();
  if (!q) return [];
  const token = await acquireGraphToken(["Mail.Read"]);
  // ImmutableId: het id blijft geldig als de mail daarna gearchiveerd/verplaatst
  // wordt — anders 404't de bijlagen-call op een mail die net is opgeruimd.
  const headers = { Authorization: `Bearer ${token}`, ConsistencyLevel: "eventual", Prefer: 'IdType="ImmutableId"' };
  // $search gebruikt KQL; quotes eromheen zodat de hele frase gezocht wordt.
  const fields = "$select=id,conversationId,subject,from,receivedDateTime,bodyPreview,webLink,hasAttachments";
  const url = `${GRAPH}/me/messages?$search=${encodeURIComponent(`"${q}"`)}&${fields}&$top=${top}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`E-mail zoeken mislukt (${res.status}): ${t.slice(0, 200)}`);
  }
  return ((await res.json()).value || []).map((m) => ({
    id: m.id,
    conversationId: m.conversationId || null,
    subject: m.subject || "(geen onderwerp)",
    from: m.from?.emailAddress?.name || m.from?.emailAddress?.address || "",
    received: m.receivedDateTime || null,
    preview: m.bodyPreview || "",
    webLink: m.webLink || null,
    hasAttachments: !!m.hasAttachments,
  }));
}

const RE_PREFIX = /^((re|fw|fwd|aw|antw)\s*:\s*)+/i;

/** Onderwerp zonder Re:/Fw:-prefixen — de naam van het gesprek. */
export const zonderPrefix = (tekst) => String(tekst || "").replace(RE_PREFIX, "").trim();

/**
 * Zoekresultaten bundelen per e-mailgesprek. Een thread levert losse treffers
 * op ("RE: …", "FW: …", "…"); als bron wil je het gesprek, niet de flarden.
 *
 * Geeft groepen terug: { conversationId, onderwerp, berichten (nieuwste eerst),
 * deelnemers, laatste }. Mail zonder conversationId blijft een eigen groep.
 */
export function groepeerPerGesprek(mails) {
  const perGesprek = new Map();
  const groepen = [];
  (mails || []).forEach((m) => {
    const sleutel = m.conversationId || `los-${m.id}`;
    if (!perGesprek.has(sleutel)) {
      const groep = { conversationId: m.conversationId || null,
                      onderwerp: zonderPrefix(m.subject) || "(geen onderwerp)",
                      berichten: [] };
      perGesprek.set(sleutel, groep);
      groepen.push(groep);
    }
    perGesprek.get(sleutel).berichten.push(m);
  });
  groepen.forEach((groep) => {
    groep.berichten.sort((a, b) =>
      String(b.received || "").localeCompare(String(a.received || "")));
    groep.laatste = groep.berichten[0] || null;
    groep.deelnemers = [...new Set(groep.berichten.map((m) => m.from).filter(Boolean))];
  });
  return groepen;
}

/** Echte bijlagen (geen inline plaatjes) van één mail — zonder inhoud. */
export async function listMailAttachments(messageId) {
  const token = await acquireGraphToken(["Mail.Read"]);
  const res = await fetch(
    `${GRAPH}/me/messages/${encodeURIComponent(messageId)}/attachments?$select=id,name,size,contentType,isInline`,
    { headers: { Authorization: `Bearer ${token}`, Prefer: 'IdType="ImmutableId"' } }
  );
  if (!res.ok) throw new Error(`Bijlagen ophalen mislukt (${res.status})`);
  return ((await res.json()).value || [])
    .filter((a) => !a.isInline)
    .map((a) => ({
      id: a.id, name: a.name || "bijlage", size: a.size || 0, contentType: a.contentType || "",
      // itemAttachment = een mail/afspraak/contact als bijlage — wel tonen,
      // niet parsebaar (heeft geen contentBytes).
      isFile: (a["@odata.type"] || "").endsWith("fileAttachment"),
    }));
}

// Boven deze grens parsen we niet: base64 → bytes → upload → parser is dan
// drie kopieën in het geheugen, en een "docx" van 100 MB is sowieso verdacht.
export const MAX_BIJLAGE_BYTES = 25 * 1024 * 1024;

/** Eén bijlage als File (base64 → bytes), om te parsen of te hergebruiken. */
export async function getMailAttachmentFile(messageId, attachmentId) {
  const token = await acquireGraphToken(["Mail.Read"]);
  const res = await fetch(
    `${GRAPH}/me/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`,
    { headers: { Authorization: `Bearer ${token}`, Prefer: 'IdType="ImmutableId"' } }
  );
  if (!res.ok) throw new Error(`Bijlage ophalen mislukt (${res.status})`);
  const a = await res.json();
  if (!a.contentBytes) throw new Error("Deze bijlage heeft geen bestandsinhoud (agenda-item of gelinkte cloud-bijlage)");
  if ((a.size || a.contentBytes.length * 0.75) > MAX_BIJLAGE_BYTES) {
    throw new Error("Bijlage is te groot om als bron te parsen (max 25 MB)");
  }
  const bin = atob(a.contentBytes);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], a.name || "bijlage", { type: a.contentType || "application/octet-stream" });
}

/**
 * Onderwerpen (genormaliseerd) van recent gearchiveerde mails. Gebruikt om
 * gearchiveerde GEVLAGDE mails uit het weekplan te filteren — een geflagde mail
 * die je archiveert is afgehandeld en hoeft niet meer in de takenlijst.
 */
export async function fetchArchivedSubjects({ top = 250 } = {}) {
  try {
    const token = await acquireGraphToken(["Mail.Read"]);
    const url = `${GRAPH}/me/mailFolders/archive/messages?$select=subject&$top=${top}&$orderby=receivedDateTime%20desc`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return new Set();
    const norm = (s) => String(s || "").replace(/^(re|fw|fwd):\s*/i, "").trim().toLowerCase();
    return new Set(((await res.json()).value || []).map((m) => norm(m.subject)).filter(Boolean));
  } catch { return new Set(); }
}

/**
 * Recente inbox-mails in Graph-shape (voor de Frits-fallback via eigen login).
 * `sinceIso` = ISO-tijdstip zonder ms, bv. "2026-07-02T06:00:00Z".
 */
export async function fetchRecentInbox(sinceIso, { top = 40 } = {}) {
  const token = await acquireGraphToken(["Mail.Read"]);
  const fields = "$select=id,conversationId,subject,from,toRecipients,ccRecipients,receivedDateTime,bodyPreview,body,webLink,isDraft";
  const url = `${GRAPH}/me/mailFolders/inbox/messages?$filter=receivedDateTime%20gt%20${sinceIso}` +
    `&$orderby=receivedDateTime%20asc&$top=${top}&${fields}`;
  const res = await fetch(url, {
    // ImmutableId: id's blijven geldig als de mail later van map wisselt (archiveren!)
    headers: { Authorization: `Bearer ${token}`, Prefer: 'IdType="ImmutableId", outlook.body-content-type="text"' },
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`Inbox ophalen mislukt (${res.status}): ${t.slice(0, 200)}`);
  }
  return (await res.json()).value || [];
}

/** Recente verzonden mails in Graph-shape (voor het tone-of-voice profiel in de fallback). */
export async function fetchRecentSent({ top = 150 } = {}) {
  const token = await acquireGraphToken(["Mail.Read"]);
  const fields = "$select=subject,toRecipients,body,sentDateTime";
  const url = `${GRAPH}/me/mailFolders/sentitems/messages?$orderby=sentDateTime%20desc&$top=${top}&${fields}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Prefer: 'outlook.body-content-type="text"' },
  });
  if (!res.ok) throw new Error(`Verzonden items ophalen mislukt (${res.status})`);
  return (await res.json()).value || [];
}

/**
 * Concept-antwoord als reply-draft in de thread zetten via de eigen login
 * (delegated Mail.ReadWrite). NOOIT verzenden — draft-only. Returnt draft-id.
 */
export async function createReplyDraft(messageId, bodyText) {
  const token = await acquireGraphToken(["Mail.ReadWrite"]);
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Prefer: 'IdType="ImmutableId"' };
  // Reply-all: Aan/CC van het origineel blijven staan (reply liet CC'ers vallen)
  const r = await fetch(`${GRAPH}/me/messages/${encodeURIComponent(messageId)}/createReplyAll`, {
    method: "POST", headers, body: "{}",
  });
  if (!r.ok) throw new Error(`createReplyAll mislukt (${r.status})`);
  const draft = await r.json();
  const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>");
  // Calibri i.p.v. Outlooks Segoe UI-fallback — matcht de gequote thread
  const html = `<div style="font-family: Calibri, Arial, sans-serif; font-size: 11pt;">${esc(bodyText)}</div>` + (draft.body?.content || "");
  const p = await fetch(`${GRAPH}/me/messages/${encodeURIComponent(draft.id)}`, {
    method: "PATCH", headers, body: JSON.stringify({ body: { contentType: "HTML", content: html } }),
  });
  if (!p.ok) console.warn(`Reply-draft body zetten mislukt (${p.status})`);
  return draft.id;
}

/**
 * Mail doorsturen naar een collega (delegeren). send=true verstuurt direct
 * (Mail.Send delegated, al in gebruik voor SRT-mails); anders een concept-forward
 * in je eigen Concepten.
 */
export async function forwardMail(messageId, toEmail, comment, { send = false } = {}) {
  const token = await acquireGraphToken([send ? "Mail.Send" : "Mail.ReadWrite"]);
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Prefer: 'IdType="ImmutableId"' };
  const action = send ? "forward" : "createForward";
  const res = await fetch(`${GRAPH}/me/messages/${encodeURIComponent(messageId)}/${action}`, {
    method: "POST", headers,
    body: JSON.stringify({ comment: String(comment || "").replace(/\n/g, "<br>"), toRecipients: [{ emailAddress: { address: toEmail } }] }),
  });
  if (send) {
    if (res.status !== 202) throw new Error(`Doorsturen mislukt (${res.status})`);
    return { sent: true };
  }
  if (!res.ok) throw new Error(`Concept-forward mislukt (${res.status})`);
  const draft = await res.json();
  return { draftId: draft.id, webLink: draft.webLink || null };
}

/** Draft verwijderen (voor concept-updates: oude reply-draft weg, nieuwe ervoor in de plaats). */
export async function deleteDraft(draftId) {
  const token = await acquireGraphToken(["Mail.ReadWrite"]);
  const res = await fetch(`${GRAPH}/me/messages/${encodeURIComponent(draftId)}`, {
    method: "DELETE", headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok && res.status !== 404) console.warn(`Draft verwijderen mislukt (${res.status})`);
}

/** Volledige (platte) tekst van één e-mail (voor context in de bron). */
export async function getMailBody(messageId) {
  const token = await acquireGraphToken(["Mail.Read"]);
  // Prefer-header: Graph converteert de body server-side naar nette platte tekst
  // (mét alinea's/regeleinden). Fallback htmlToPlainText dekt het geval dat de
  // header genegeerd wordt — kaal textContent plakte alle alinea's aan elkaar.
  const res = await fetch(
    `${GRAPH}/me/messages/${messageId}?$select=subject,body`,
    { headers: { Authorization: `Bearer ${token}`, Prefer: 'outlook.body-content-type="text", IdType="ImmutableId"' } }
  );
  if (!res.ok) throw new Error(`E-mail ophalen mislukt (${res.status})`);
  const m = await res.json();
  const content = m.body?.content || "";
  if ((m.body?.contentType || "").toLowerCase() === "html") {
    return htmlToPlainText(content);
  }
  return String(content).replace(/\n{3,}/g, "\n\n").trim();
}

// Elke reply herhaalt de hele voorgaande mail. Bij een gesprek van 8 berichten
// staat het eerste bericht dus 8x in de tekst — knippen bij de eerste
// quote-marker houdt het leesbaar (en past binnen het token-budget).
const QUOTE_MARKERS = [
  /^-{2,}\s*(oorspronkelijk bericht|original message|forwarded message)/im,
  /^\s*(van|from|verzonden|sent):\s*.+$/im,
  /^\s*op .{5,60}\s+schreef\s+.{2,80}:\s*$/im,
  /^\s*on .{5,60}\s+wrote:\s*$/im,
  /^\s*>{1,}\s?.+$/m,
];
function stripQuotedHistory(text) {
  let cut = String(text || "");
  let index = cut.length;
  for (const re of QUOTE_MARKERS) {
    const hit = re.exec(cut);
    // Alleen knippen als er nog een fatsoenlijk stuk eigen tekst vóór staat —
    // anders houd je van een kort antwoord ("Prima!") niets over.
    if (hit && hit.index > 40 && hit.index < index) index = hit.index;
  }
  return cut.slice(0, index).replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Alle berichten van één e-mailgesprek (conversation) uit de eigen mailbox —
 * inclusief de verzonden antwoorden, op tijd gesorteerd. Body zit erbij, dus
 * dit is één Graph-call voor het hele gesprek.
 */
export async function listConversationMessages(conversationId, { top = 50 } = {}) {
  const token = await acquireGraphToken(["Mail.Read"]);
  const fields = "$select=id,conversationId,subject,from,toRecipients,receivedDateTime,body,webLink,hasAttachments";
  // $orderby samen met een $filter op conversationId geeft "restriction too
  // complex" — daarom client-side sorteren.
  const url = `${GRAPH}/me/messages?$filter=conversationId%20eq%20'${encodeURIComponent(conversationId)}'&${fields}&$top=${top}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Prefer: 'outlook.body-content-type="text", IdType="ImmutableId"',
    },
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`Gesprek ophalen mislukt (${res.status}): ${t.slice(0, 200)}`);
  }
  const msgs = ((await res.json()).value || []).map((m) => {
    const raw = m.body?.content || "";
    const plat = (m.body?.contentType || "").toLowerCase() === "html" ? htmlToPlainText(raw) : String(raw);
    return {
      id: m.id,
      subject: m.subject || "(geen onderwerp)",
      from: m.from?.emailAddress?.name || m.from?.emailAddress?.address || "",
      to: (m.toRecipients || []).map((r) => r.emailAddress?.name || r.emailAddress?.address).filter(Boolean),
      received: m.receivedDateTime || null,
      webLink: m.webLink || null,
      hasAttachments: !!m.hasAttachments,
      text: stripQuotedHistory(plat),
    };
  });
  msgs.sort((a, b) => String(a.received || "").localeCompare(String(b.received || "")));
  // Sent Items + Inbox kunnen hetzelfde bericht bevatten (bv. mail aan jezelf/CC).
  const gezien = new Set();
  return msgs.filter((m) => {
    const sleutel = `${m.from}|${m.text.slice(0, 300)}`;
    if (gezien.has(sleutel)) return false;
    gezien.add(sleutel);
    return true;
  });
}

/** Het hele gesprek als één leesbaar tekstblok (chronologisch). */
export async function getConversationText(conversationId, { maxChars = 20000 } = {}) {
  const msgs = await listConversationMessages(conversationId);
  const blokken = msgs.map((m, i) => {
    const kop = [`[${i + 1}]`, m.received ? String(m.received).slice(0, 10) : "", m.from ? `van ${m.from}` : ""]
      .filter(Boolean).join(" · ");
    return `${kop}\n${m.text || "(lege mail)"}`;
  });
  // Budget van achteren naar voren: de nieuwste berichten (laatste scope, deadline,
  // correctie) blijven altijd staan; oudere vallen als eerste weg, met een melding.
  const scheiding = "\n\n---\n\n";
  const gekozen = [];
  let lengte = 0;
  for (let i = blokken.length - 1; i >= 0; i -= 1) {
    const extra = blokken[i].length + (gekozen.length ? scheiding.length : 0);
    if (lengte + extra > maxChars && gekozen.length) break;
    gekozen.unshift(blokken[i]);
    lengte += extra;
  }
  const weggelaten = blokken.length - gekozen.length;
  const melding = weggelaten > 0 ? `[… ${weggelaten} oudere bericht${weggelaten === 1 ? "" : "en"} weggelaten …]${scheiding}` : "";
  // Past het geheel niet (één heel lang bericht, of de melding erbij): kort het
  // OUDSTE gekozen blok aan het eind in — nooit de melding of de nieuwste mail.
  const overschot = melding.length + lengte - maxChars;
  if (overschot > 0 && gekozen.length) {
    const kort = Math.max(0, gekozen[0].length - overschot - 6);
    gekozen[0] = `${gekozen[0].slice(0, kort)} […]`;
  }
  return { aantal: msgs.length, tekst: melding + gekozen.join(scheiding) };
}

/** Nieuw Outlook-concept (in map Concepten) met optionele ontvanger(s) en
 *  PDF-bijlage(n) — voor o.a. de begeleidende offerte-e-mail. Draft-only. */
export async function createDraftMessage({ subject, body, to = [], attachments = [] }) {
  const token = await acquireGraphToken(["Mail.ReadWrite"]);
  const res = await fetch(`${GRAPH}/me/messages`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`, "Content-Type": "application/json",
      Prefer: 'IdType="ImmutableId"',
    },
    body: JSON.stringify({
      subject: subject || "",
      body: { contentType: "Text", content: body || "" },
      toRecipients: to.filter(Boolean).map((a) => ({ emailAddress: { address: a } })),
      attachments: attachments.map((att) => ({
        "@odata.type": "#microsoft.graph.fileAttachment",
        name: att.name, contentType: att.contentType || "application/pdf",
        contentBytes: att.base64,
      })),
    }),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`Concept aanmaken mislukt (${res.status}): ${t.slice(0, 150)}`);
  }
  const m = await res.json();
  return { id: m.id, webLink: m.webLink || null };
}
